// components/profile/ProfileSkeleton.tsx
import React from "react";

const ProfileSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 animate-pulse">
      <div className="">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="space-y-2">
              <div className="h-8 w-48 bg-gray-200 rounded-lg"></div>
              <div className="h-4 w-64 bg-gray-200 rounded-lg"></div>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-10 w-24 bg-gray-200 rounded-full"
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Grid Skeleton */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
                  <div className="h-6 w-40 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="h-10 w-20 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    <div className="h-12 w-full bg-gray-200 rounded-xl"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Address Information Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
                  <div className="h-6 w-40 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="h-10 w-20 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className={`space-y-2 ${i === 1 ? "md:col-span-2" : ""}`}
                  >
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    <div className="h-12 w-full bg-gray-200 rounded-xl"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Skills & Preferences Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
                  <div className="h-6 w-48 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="h-10 w-20 bg-gray-200 rounded-lg"></div>
              </div>

              {/* Skills */}
              <div className="mb-6">
                <div className="h-4 w-20 bg-gray-200 rounded mb-3"></div>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      className="h-8 w-24 bg-gray-200 rounded-full"
                    ></div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div className="mb-6">
                <div className="h-4 w-24 bg-gray-200 rounded mb-3"></div>
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-12 w-full bg-gray-200 rounded-xl"
                    ></div>
                  ))}
                </div>
              </div>

              {/* Preferences */}
              <div>
                <div className="h-4 w-32 bg-gray-200 rounded mb-3"></div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-12 bg-gray-200 rounded-xl"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* SSN Verification Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                <div className="space-y-2">
                  <div className="h-5 w-32 bg-gray-200 rounded"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded"></div>
                </div>
              </div>
              <div className="h-4 w-full bg-gray-200 rounded mb-4"></div>
              <div className="h-4 w-3/4 bg-gray-200 rounded mb-6"></div>
              <div className="h-12 w-full bg-gray-200 rounded-xl"></div>
            </div>

            {/* Document Verification Skeleton */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                <div className="space-y-2">
                  <div className="h-5 w-40 bg-gray-200 rounded"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded"></div>
                </div>
              </div>
              <div className="h-4 w-full bg-gray-200 rounded mb-4"></div>
              <div className="h-4 w-3/4 bg-gray-200 rounded mb-6"></div>
              <div className="h-12 w-full bg-gray-200 rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSkeleton;
